// Members/companies Vuex module 
import memberService from '@/services/memberService';
import mediaService from '@/services/mediaService';
import axios from 'axios';
import { getMediaUrl } from '@/utils/mediaUtils';

export default {
  namespaced: true,
  
  state: {
    members: [],
    currentMember: null,
    memberProjects: [],
    featuredMembers: [],
    searchResults: [],
    loading: false,
    error: null
  },
  
  getters: {
    allMembers: state => state.members,
    currentMember: state => state.currentMember,
    memberProjects: state => state.memberProjects,
    featuredMembers: state => state.featuredMembers,
    searchResults: state => state.searchResults,
    isLoading: state => state.loading,
    hasError: state => state.error !== null,
    getErrorMessage: state => state.error
  },
  
  mutations: {
    SET_MEMBERS(state, members) {
      state.members = members.map(member => ({
        ...member,
        // Process any media fields to ensure they use the proper URL format
        profile_image: getMediaUrl(member.profile_image)
      }));
    },
    SET_CURRENT_MEMBER(state, member) {
      state.currentMember = member;
    },
    SET_MEMBER_PROJECTS(state, projects) {
      state.memberProjects = projects;
    },
    SET_FEATURED_MEMBERS(state, members) {
      state.featuredMembers = members;
    },
    SET_SEARCH_RESULTS(state, results) {
      state.searchResults = results;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    }
  },
  
  actions: {
    /**
     * Fetch all members/companies
     */
    async fetchMembers({ commit }) {
      try {
        commit('SET_LOADING', true);
        const response = await axios.get('/api/members/');
        
        // Process the members data with proper media URLs that work with proxy
        const members = await Promise.all(response.data.map(async member => {
          return {
            ...member,
            // Use the mediaService to get correct URLs with proxy handling
            logo: member.logo ? await mediaService.getMediaUrl(member.logo, 'thumbnail') : null,
            image: member.image ? await mediaService.getMediaUrl(member.image, 'thumbnail') : null,
          };
        }));
        
        commit('SET_MEMBERS', members);
      } catch (error) {
        commit('SET_ERROR', error.message);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch a specific member by ID
     */
    async fetchMember({ commit }, id) {
      try {
        commit('SET_LOADING', true);
        const response = await axios.get(`/api/members/${id}/`);
        
        // Process the member data with proper media URLs
        const member = {
          ...response.data,
          logo: response.data.logo ? await mediaService.getMediaUrl(response.data.logo, 'thumbnail') : null,
          image: response.data.image ? await mediaService.getMediaUrl(response.data.image, 'thumbnail') : null
        };
        
        commit('SET_CURRENT_MEMBER', member);
      } catch (error) {
        commit('SET_ERROR', error.message);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch projects associated with a specific member
     */
    async fetchMemberProjects({ commit }, id) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await memberService.getMemberProjects(id);
        commit('SET_MEMBER_PROJECTS', response.data.results || response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch member projects');
        console.error('Error fetching member projects:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Fetch featured members
     */
    async fetchFeaturedMembers({ commit }) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await memberService.getFeaturedMembers();
        commit('SET_FEATURED_MEMBERS', response.data.results || response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to fetch featured members');
        console.error('Error fetching featured members:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Search members by name or description
     */
    async searchMembers({ commit }, query) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await memberService.searchMembers(query);
        commit('SET_SEARCH_RESULTS', response.data.results || response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to search members');
        console.error('Error searching members:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Filter members by criteria
     */
    async filterMembers({ commit }, filters) {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');
      
      try {
        const response = await memberService.filterMembers(filters);
        commit('SET_MEMBERS', response.data.results || response.data);
      } catch (error) {
        commit('SET_ERROR', error.message || 'Failed to filter members');
        console.error('Error filtering members:', error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    /**
     * Clear the current member details
     */
    clearCurrentMember({ commit }) {
      commit('SET_CURRENT_MEMBER', null);
    },
    
    /**
     * Clear search results
     */
    clearSearchResults({ commit }) {
      commit('SET_SEARCH_RESULTS', []);
    }
  }
}; 